import type { Writable } from 'svelte/store';
import { safePersistedStore } from '../../../../utils/safePersistedStore/safePersistedStore';
import { scrollToTop } from '../../../../utils/scrollToTop';

export const FormInitialState = {
	step: 0,
	data: [] as Record<string, string | boolean | null>[]
};

export type FormStore = ReturnType<typeof createFormStore>;

export type Form = Record<
	string,
	{ step: number; data: Record<string, string | boolean | null>[] }
>;

export const createFormStore = (key: string) => {
	// Holds the chainalysis error state (only used during NFT redemption)
	const chainalysisError = safePersistedStore(`${key}-ready`, false);

	// Holds the form state, it is stored in the local storage when available
	// Automatically hydrated if the user refreshes the page
	const form = safePersistedStore(`${key}-checkout`, { all: FormInitialState }) as Writable<Form>;

	// Resets the form state from a given step index
	const resetFromStep = (dropPageSlug: string, step: number) => {
		form.update(($currentForm: Form) => ({
			...$currentForm,
			[dropPageSlug]: {
				...$currentForm[dropPageSlug],
				step,
				data: $currentForm[dropPageSlug].data.slice(0, step)
			}
		}));
	};

	// Resets the form state from a given step index but keep the data of the step after the corresponding to the index
	// Mostly used when there is a verification step in the middle of the flow (like 2FA during the vault withdrawal)
	const resetFromStepAndKeepData = (dropPageSlug: string, step: number) => {
		form.update(($currentForm: Form) => ({
			...$currentForm,
			[dropPageSlug]: {
				...$currentForm[dropPageSlug],
				step,
				data: $currentForm[dropPageSlug].data.slice(0, step + 1)
			}
		}));
	};

	// Resets the form to its initial state so the user has to start from scratch
	const clearForm = (dropPageSlug?: string) => {
		if (!dropPageSlug) {
			form.set({ all: FormInitialState });
			return;
		}

		form.update(($currentForm: Form) => {
			const { [dropPageSlug]: _, ...restCurrentForm } = $currentForm;
			return restCurrentForm;
		});
	};

	const updateForm = (dropPageSlug: string, newForm: Partial<typeof FormInitialState>) => {
		form.update(($currentForm: Form) => {
			return {
				...$currentForm,
				[dropPageSlug]: {
					...$currentForm[dropPageSlug],
					...newForm
				}
			};
		});
	};

	const goToStep = (dropPageSlug: string, step: number) => {
		form.update(($currentForm: Form) => {
			return {
				...$currentForm,
				[dropPageSlug]: {
					...$currentForm[dropPageSlug],
					step
				}
			};
		});

		scrollToTop();
	};

	// Redirects the user to the previous step but keep the data related to the next ones
	const goToPreviousStep = (dropPageSlug: string) => {
		form.update(($currentForm: Form) => {
			return {
				...$currentForm,
				[dropPageSlug]: {
					...$currentForm[dropPageSlug],
					step: $currentForm[dropPageSlug].step - 1
				}
			};
		});

		scrollToTop();
	};

	// Saves the provided data and redirect the user to the next step
	const saveStep = (
		dropPageSlug: string,
		data: Record<string, string | boolean | null>,
		stayOnTheSameStep: boolean = false
	) => {
		form.update(($currentForm: Form) => {
			return {
				...$currentForm,
				[dropPageSlug]: {
					...$currentForm[dropPageSlug],
					step: ($currentForm[dropPageSlug]?.step || 0) + (stayOnTheSameStep ? 0 : 1),
					data: [
						...($currentForm[dropPageSlug]?.data || []).slice(
							0,
							$currentForm[dropPageSlug]?.step || 0
						),
						data,
						...($currentForm[dropPageSlug]?.data || []).slice(
							($currentForm[dropPageSlug]?.step || 0) + 1
						)
					]
				}
			};
		});

		if (!stayOnTheSameStep) {
			scrollToTop();
		}
	};

	// Sets the Chainalysis error state to true when a Chainalysis alert has been emitted (only used during NFT redemption)
	const enableChainalysisError = () => chainalysisError.set(true);

	return {
		clearForm,
		form,
		updateForm,
		saveStep,
		goToStep,
		goToPreviousStep,
		resetFromStep,
		resetFromStepAndKeepData,
		chainalysisError,
		enableChainalysisError
	};
};
